import React, { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import {
  Plus, Loader2, AlertTriangle, FileCheck, FileEdit, ShieldAlert, ChevronRight, Trash2, X, Sparkles,
} from 'lucide-react';
import { tl } from '../../lib/testingLabApi';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const SEVERITY_COLOR = {
  critical: 'bg-red-500 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-amber-500 text-white',
  low: 'bg-slate-400 text-white',
};

const STATUS_COLOR = {
  new: 'bg-blue-500/20 text-blue-700 dark:text-blue-300',
  confirmed: 'bg-blue-500/30 text-blue-700 dark:text-blue-300',
  needs_more_evidence: 'bg-amber-500/20 text-amber-700 dark:text-amber-300',
  prompt_generated: 'bg-violet-500/20 text-violet-700 dark:text-violet-300',
  sent_to_code_agent: 'bg-violet-500/30 text-violet-700 dark:text-violet-300',
  fix_in_progress: 'bg-indigo-500/20 text-indigo-700 dark:text-indigo-300',
  ready_for_retest: 'bg-cyan-500/20 text-cyan-700 dark:text-cyan-300',
  retest_failed: 'bg-red-500/20 text-red-700 dark:text-red-300',
  retest_passed: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300',
  fully_resolved: 'bg-emerald-500 text-white',
  archived: 'bg-slate-500/20 text-muted-foreground',
};

const STATUS_OPTIONS = Object.keys(STATUS_COLOR);

export const FindingsTab = ({ activeSessionId = null }) => {
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await tl.listFindings(activeSessionId ? { session_id: activeSessionId } : {});
      setFindings(data.items || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [activeSessionId]);

  const create = async (body) => {
    setCreating(true);
    try {
      const f = await tl.createFinding({ ...body, test_session_id: activeSessionId });
      setShowCreate(false);
      await load();
      setSelected(f);
    } catch (e) { toast.error(e.message); }
    finally { setCreating(false); }
  };

  return (
    <div className="grid grid-cols-12 gap-4 h-[calc(100vh-220px)]" data-testid="findings-tab">
      <div className="col-span-5">
        <Card className="h-full flex flex-col">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold">Findings</h3>
              <p className="text-[11px] text-muted-foreground">{findings.length} total</p>
            </div>
            <Button size="sm" onClick={() => setShowCreate(true)} data-testid="finding-new">
              <Plus className="w-3 h-3 mr-1" /> New
            </Button>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1.5">
              {loading ? (
                <div className="text-center py-8 text-xs text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> loading
                </div>
              ) : findings.length === 0 ? (
                <div className="text-center py-12 text-xs text-muted-foreground">
                  No findings yet. Create one when you spot something off.
                </div>
              ) : (
                findings.map((f) => (
                  <button
                    key={f.finding_id}
                    onClick={() => setSelected(f)}
                    className={cn(
                      'w-full text-left p-2.5 border rounded-md transition-colors',
                      selected?.finding_id === f.finding_id
                        ? 'border-violet-500 bg-violet-500/5'
                        : 'border-border hover:border-violet-500/50'
                    )}
                    data-testid={`finding-row-${f.finding_id}`}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <Badge className={`${SEVERITY_COLOR[f.severity]} text-[9px] h-4`}>{f.severity}</Badge>
                      <Badge className={`${STATUS_COLOR[f.status]} text-[9px] h-4 border-0`}>{f.status}</Badge>
                    </div>
                    <div className="text-xs font-medium line-clamp-2">{f.title}</div>
                    {f.observed_behavior && (
                      <div className="text-[10px] text-muted-foreground line-clamp-1 mt-0.5">{f.observed_behavior}</div>
                    )}
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </Card>
      </div>
      <div className="col-span-7">
        {selected ? (
          <FindingDetail finding={selected} onUpdate={(f) => { setSelected(f); load(); }} />
        ) : (
          <Card className="h-full flex items-center justify-center text-muted-foreground text-sm">
            Select a finding to inspect
          </Card>
        )}
      </div>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="finding-create-dialog">
          <DialogHeader><DialogTitle>New finding</DialogTitle></DialogHeader>
          <CreateFindingForm onSubmit={create} disabled={creating} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CreateFindingForm = ({ onSubmit, disabled }) => {
  const [title, setTitle] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [observed, setObserved] = useState('');
  const [expected, setExpected] = useState('');
  return (
    <div className="space-y-3">
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" data-testid="finding-title-input" />
      <Select value={severity} onValueChange={setSeverity}>
        <SelectTrigger data-testid="finding-severity-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="critical">Critical</SelectItem>
          <SelectItem value="high">High</SelectItem>
          <SelectItem value="medium">Medium</SelectItem>
          <SelectItem value="low">Low</SelectItem>
        </SelectContent>
      </Select>
      <Textarea value={observed} onChange={(e) => setObserved(e.target.value)} placeholder="Observed behavior" data-testid="finding-observed" />
      <Textarea value={expected} onChange={(e) => setExpected(e.target.value)} placeholder="Expected behavior" data-testid="finding-expected" />
      <DialogFooter>
        <Button onClick={() => onSubmit({ title, severity, observed_behavior: observed, expected_behavior: expected })} disabled={!title || disabled} data-testid="finding-create-submit">
          {disabled ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
        </Button>
      </DialogFooter>
    </div>
  );
};

const FindingDetail = ({ finding, onUpdate }) => {
  const [busy, setBusy] = useState(false);
  const [fixPrompts, setFixPrompts] = useState([]);
  const [riskAudits, setRiskAudits] = useState([]);
  const [openPrompt, setOpenPrompt] = useState(null);
  const [resolutionData, setResolutionData] = useState({
    code_agent_output_link: finding.code_agent_output_link || '',
    pr_link: finding.pr_link || '',
    regression_test_added: finding.regression_test_added,
    documentation_updated: finding.documentation_updated,
    documentation_not_required_reason: finding.documentation_not_required_reason || '',
    resolution_summary: finding.resolution_summary || '',
  });

  useEffect(() => {
    setResolutionData({
      code_agent_output_link: finding.code_agent_output_link || '',
      pr_link: finding.pr_link || '',
      regression_test_added: finding.regression_test_added,
      documentation_updated: finding.documentation_updated,
      documentation_not_required_reason: finding.documentation_not_required_reason || '',
      resolution_summary: finding.resolution_summary || '',
    });
    const loadPrompts = async () => {
      try {
        const [fp, ra] = await Promise.all([tl.fixPrompts(finding.finding_id), tl.riskAudits(finding.finding_id)]);
        setFixPrompts(fp.items || []);
        setRiskAudits(ra.items || []);
      } catch { /* ignore */ }
    };
    loadPrompts();
  }, [finding.finding_id]);  // eslint-disable-line

  const askAkroMSC = async () => {
    setBusy(true);
    try {
      const res = await tl.akroMSC({ finding_id: finding.finding_id });
      if (res.error) toast.error(`Akro: ${res.error}`);
      else {
        toast.success('Akro generated Must/Should/Could');
        const updated = await tl.getFinding(finding.finding_id);
        onUpdate(updated);
      }
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const askAkroFix = async () => {
    setBusy(true);
    try {
      const fp = await tl.akroFixPrompt(finding.finding_id);
      toast.success('Fix prompt generated');
      const updated = await tl.getFinding(finding.finding_id);
      onUpdate(updated);
      const fpList = await tl.fixPrompts(finding.finding_id);
      setFixPrompts(fpList.items || []);
      setOpenPrompt({ type: 'fix', text: fp.prompt_text });
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const askAkroRisk = async () => {
    setBusy(true);
    try {
      const rp = await tl.akroRiskAudit(finding.finding_id, finding.resolution_summary);
      toast.success('Risk audit prompt generated');
      const raList = await tl.riskAudits(finding.finding_id);
      setRiskAudits(raList.items || []);
      setOpenPrompt({ type: 'risk', text: rp.prompt_text });
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const updateStatus = async (status) => {
    setBusy(true);
    try {
      const u = await tl.updateFinding(finding.finding_id, { status });
      onUpdate(u);
    } catch (e) {
      const msg = typeof e.detail === 'object' && e.detail.missing
        ? `Cannot mark Fully Resolved — missing: ${e.detail.missing.join(', ')}`
        : e.message;
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const saveResolution = async () => {
    setBusy(true);
    try {
      const u = await tl.updateFinding(finding.finding_id, resolutionData);
      onUpdate(u);
      toast.success('Resolution data saved');
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Card className="h-full flex flex-col" data-testid="finding-detail">
      <div className="px-4 py-3 border-b border-border space-y-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={`${SEVERITY_COLOR[finding.severity]} text-[10px]`}>{finding.severity}</Badge>
          <Select value={finding.status} onValueChange={updateStatus}>
            <SelectTrigger className="h-6 text-[10px] w-auto px-2" data-testid="finding-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <h2 className="text-base font-semibold">{finding.title}</h2>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          <Section title="Observed">{finding.observed_behavior || <em className="text-muted-foreground">unset</em>}</Section>
          <Section title="Expected">{finding.expected_behavior || <em className="text-muted-foreground">unset</em>}</Section>

          <div className="grid grid-cols-3 gap-2">
            <Button size="sm" variant="outline" onClick={askAkroMSC} disabled={busy} data-testid="akro-msc-btn">
              <Sparkles className="w-3 h-3 mr-1" /> Akro: M/S/C
            </Button>
            <Button size="sm" variant="outline" onClick={askAkroFix} disabled={busy} data-testid="akro-fix-btn">
              <FileEdit className="w-3 h-3 mr-1" /> Fix Prompt
            </Button>
            <Button size="sm" variant="outline" onClick={askAkroRisk} disabled={busy} data-testid="akro-risk-btn">
              <ShieldAlert className="w-3 h-3 mr-1" /> Risk Audit
            </Button>
          </div>

          {(finding.must_work?.length || 0) + (finding.should_work?.length || 0) + (finding.could_work?.length || 0) > 0 && (
            <div className="grid grid-cols-3 gap-2 text-xs">
              <ListBox title="Must" items={finding.must_work} color="text-red-600 dark:text-red-400" />
              <ListBox title="Should" items={finding.should_work} color="text-amber-600 dark:text-amber-400" />
              <ListBox title="Could" items={finding.could_work} color="text-blue-600 dark:text-blue-400" />
            </div>
          )}

          {finding.acceptance_criteria?.length > 0 && (
            <Section title="Acceptance criteria">
              <ul className="text-xs space-y-1">
                {finding.acceptance_criteria.map((c, i) => <li key={i} className="flex gap-2"><span>☐</span><span>{c}</span></li>)}
              </ul>
            </Section>
          )}

          {fixPrompts.length > 0 && (
            <Section title="Fix prompts">
              {fixPrompts.map((fp) => (
                <button key={fp.fix_prompt_id} onClick={() => setOpenPrompt({ type: 'fix', text: fp.prompt_text })}
                  className="w-full text-left p-2 border border-border rounded text-xs hover:bg-accent flex items-center justify-between mb-1"
                  data-testid={`fix-prompt-${fp.fix_prompt_id}`}>
                  <span>Fix prompt · {new Date(fp.created_at).toLocaleString()}</span>
                  <ChevronRight className="w-3 h-3" />
                </button>
              ))}
            </Section>
          )}

          {riskAudits.length > 0 && (
            <Section title="Risk audits">
              {riskAudits.map((rp) => (
                <button key={rp.risk_prompt_id} onClick={() => setOpenPrompt({ type: 'risk', text: rp.prompt_text })}
                  className="w-full text-left p-2 border border-border rounded text-xs hover:bg-accent flex items-center justify-between mb-1"
                  data-testid={`risk-prompt-${rp.risk_prompt_id}`}>
                  <span>Risk audit · {new Date(rp.created_at).toLocaleString()}</span>
                  <ChevronRight className="w-3 h-3" />
                </button>
              ))}
            </Section>
          )}

          <Section title="Fully Resolved checklist">
            <div className="space-y-2">
              <Input placeholder="Code agent output link"
                value={resolutionData.code_agent_output_link}
                onChange={(e) => setResolutionData({ ...resolutionData, code_agent_output_link: e.target.value })}
                className="h-8 text-xs" data-testid="resolution-output-link" />
              <Input placeholder="PR link (optional)"
                value={resolutionData.pr_link}
                onChange={(e) => setResolutionData({ ...resolutionData, pr_link: e.target.value })}
                className="h-8 text-xs" data-testid="resolution-pr-link" />
              <div className="flex items-center gap-3 text-xs">
                <span>Regression test added?</span>
                <Button size="sm" variant={resolutionData.regression_test_added === true ? 'default' : 'outline'} className="h-7 text-xs"
                  onClick={() => setResolutionData({ ...resolutionData, regression_test_added: true })}
                  data-testid="resolution-regression-yes">Yes</Button>
                <Button size="sm" variant={resolutionData.regression_test_added === false ? 'default' : 'outline'} className="h-7 text-xs"
                  onClick={() => setResolutionData({ ...resolutionData, regression_test_added: false })}
                  data-testid="resolution-regression-no">No</Button>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span>Documentation updated?</span>
                <Button size="sm" variant={resolutionData.documentation_updated === true ? 'default' : 'outline'} className="h-7 text-xs"
                  onClick={() => setResolutionData({ ...resolutionData, documentation_updated: true })}
                  data-testid="resolution-docs-yes">Yes</Button>
                <Button size="sm" variant={resolutionData.documentation_updated === false ? 'default' : 'outline'} className="h-7 text-xs"
                  onClick={() => setResolutionData({ ...resolutionData, documentation_updated: false })}
                  data-testid="resolution-docs-no">No</Button>
              </div>
              {resolutionData.documentation_updated === false && (
                <Input placeholder="Why is no doc update needed?"
                  value={resolutionData.documentation_not_required_reason}
                  onChange={(e) => setResolutionData({ ...resolutionData, documentation_not_required_reason: e.target.value })}
                  className="h-8 text-xs" data-testid="resolution-docs-reason" />
              )}
              <Textarea placeholder="Resolution summary (what changed)"
                value={resolutionData.resolution_summary}
                onChange={(e) => setResolutionData({ ...resolutionData, resolution_summary: e.target.value })}
                className="min-h-[60px] text-xs" data-testid="resolution-summary" />
              <Button size="sm" onClick={saveResolution} disabled={busy} className="w-full h-8" data-testid="resolution-save">
                Save resolution data
              </Button>
            </div>
          </Section>
        </div>
      </ScrollArea>

      <Dialog open={!!openPrompt} onOpenChange={(o) => !o && setOpenPrompt(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="prompt-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {openPrompt?.type === 'fix' ? <FileEdit className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />}
              {openPrompt?.type === 'fix' ? 'Emergent Fix Prompt' : 'Risk Audit Prompt'}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="flex-1 border border-border rounded p-3 bg-muted/30">
            <pre className="text-xs whitespace-pre-wrap font-mono">{openPrompt?.text}</pre>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => navigator.clipboard.writeText(openPrompt?.text || '').then(() => toast.success('Copied'))} data-testid="prompt-copy">
              Copy to clipboard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const Section = ({ title, children }) => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">{title}</div>
    <div className="text-xs">{children}</div>
  </div>
);

const ListBox = ({ title, items, color }) => (
  <div className="border border-border rounded p-2 bg-muted/30">
    <div className={`text-[10px] font-semibold uppercase tracking-wider mb-1 ${color}`}>{title}</div>
    <ul className="space-y-1">
      {(items || []).map((it, i) => <li key={i} className="text-[11px]">• {it}</li>)}
    </ul>
  </div>
);

export default FindingsTab;
